import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  wrapper: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    margin: '4px 0 20px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 0',
    color: '#9ca3af',
    fontSize: 14,
  },
  summaryBarContainer: {
    marginBottom: 24,
  },
  summaryBarLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 6,
    fontSize: 12,
    color: '#6b7280',
  },
  summaryBarTrack: {
    width: '100%',
    height: 28,
    borderRadius: 6,
    overflow: 'hidden',
    display: 'flex',
    background: '#f3f4f6',
  },
  summaryBarAvailable: {
    height: '100%',
    background: '#22c55e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
    transition: 'width 0.4s ease',
  },
  summaryBarNotAvailable: {
    height: '100%',
    background: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
    transition: 'width 0.4s ease',
  },
  columnsContainer: {
    display: 'flex',
    gap: 20,
    marginBottom: 24,
  },
  column: {
    flex: 1,
    minWidth: 0,
  },
  columnHeader: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  columnHeaderAvailable: {
    color: '#16a34a',
  },
  columnHeaderNotAvailable: {
    color: '#dc2626',
  },
  indicator: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  indicatorGreen: {
    background: '#22c55e',
  },
  indicatorRed: {
    background: '#ef4444',
  },
  indicatorAmber: {
    background: '#f59e0b',
  },
  stateCard: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'default',
    position: 'relative',
    transition: 'box-shadow 0.15s ease',
  },
  stateCardHover: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  stateAbbr: {
    fontSize: 15,
    fontWeight: 700,
    color: '#111827',
  },
  cityList: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
    lineHeight: 1.5,
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#e5e7eb',
    color: '#374151',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 10,
    padding: '2px 8px',
    marginLeft: 8,
  },
  stateCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tooltip: {
    position: 'fixed',
    background: '#1f2937',
    color: '#ffffff',
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.5,
    pointerEvents: 'none',
    zIndex: 10000,
    maxWidth: 260,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    whiteSpace: 'pre-line',
  },
  edgeCasesSection: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: 20,
  },
  edgeCasesTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#92400e',
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  edgeCaseCard: {
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 6,
    padding: '12px 14px',
    marginBottom: 8,
    cursor: 'default',
    position: 'relative',
    transition: 'box-shadow 0.15s ease',
  },
  edgeCaseHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  edgeCaseState: {
    fontSize: 15,
    fontWeight: 700,
    color: '#92400e',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  edgeCasePct: {
    fontSize: 13,
    fontWeight: 600,
    color: '#b45309',
  },
  pctBarTrack: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    background: '#fde68a',
    overflow: 'hidden',
  },
  pctBarFill: {
    height: '100%',
    borderRadius: 4,
    background: '#f59e0b',
    transition: 'width 0.4s ease',
  },
  edgeCaseDetail: {
    fontSize: 11,
    color: '#92400e',
    marginTop: 6,
  },
  stateCardsList: {
    maxHeight: 480,
    overflowY: 'auto',
    paddingRight: 4,
  },
};

function groupByState(entries) {
  const map = {};
  for (const entry of entries) {
    if (!map[entry.state]) {
      map[entry.state] = { state: entry.state, cities: [], totalAddresses: 0 };
    }
    map[entry.state].cities.push({ city: entry.city, addressCount: entry.addressCount });
    map[entry.state].totalAddresses += entry.addressCount;
  }
  return Object.values(map).sort((a, b) => a.state.localeCompare(b.state));
}

function StateCard({ group, variant, onMouseEnter, onMouseLeave }) {
  const [hovered, setHovered] = useState(false);

  const indicatorStyle = {
    ...styles.indicator,
    ...(variant === 'available' ? styles.indicatorGreen : variant === 'notAvailable' ? styles.indicatorRed : styles.indicatorAmber),
  };

  const cardStyle = {
    ...styles.stateCard,
    ...(hovered ? styles.stateCardHover : {}),
  };

  const handleMouseEnter = (e) => {
    setHovered(true);
    if (onMouseEnter) onMouseEnter(e, group);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (onMouseLeave) onMouseLeave();
  };

  return (
    <div style={cardStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div style={styles.stateCardHeader}>
        <span style={indicatorStyle} />
        <span style={styles.stateAbbr}>{group.state}</span>
        <span style={styles.countBadge}>
          {group.totalAddresses} {group.totalAddresses === 1 ? 'address' : 'addresses'}
        </span>
      </div>
      <div style={styles.cityList}>
        {group.cities.map((c, i) => (
          <span key={c.city}>
            {c.city} ({c.addressCount}){i < group.cities.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function EdgeCaseCard({ edgeCase, onMouseEnter, onMouseLeave }) {
  const [hovered, setHovered] = useState(false);

  const cardStyle = {
    ...styles.edgeCaseCard,
    ...(hovered ? styles.stateCardHover : {}),
  };

  const handleMouseEnter = (e) => {
    setHovered(true);
    if (onMouseEnter) onMouseEnter(e, edgeCase);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (onMouseLeave) onMouseLeave();
  };

  return (
    <div style={cardStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div style={styles.edgeCaseHeader}>
        <div style={styles.edgeCaseState}>
          <span style={{ ...styles.indicator, ...styles.indicatorAmber }} />
          {edgeCase.state}
          {edgeCase.mixedAvailability && (
            <span style={{ fontSize: 11, fontWeight: 400, color: '#b45309' }}>Mixed</span>
          )}
        </div>
        <span style={styles.edgeCasePct}>{edgeCase.pctAvailable.toFixed(1)}% available</span>
      </div>
      <div style={styles.pctBarTrack}>
        <div style={{ ...styles.pctBarFill, width: `${edgeCase.pctAvailable}%` }} />
      </div>
      <div style={styles.edgeCaseDetail}>
        {edgeCase.availableCount} of {edgeCase.totalCount} addresses have service
      </div>
    </div>
  );
}

export default function MosquitoAvailabilityBoundaryMap({ data }) {
  const [tooltip, setTooltip] = useState(null);

  const availableGroups = useMemo(() => {
    if (!data || !data.available) return [];
    return groupByState(data.available);
  }, [data]);

  const notAvailableGroups = useMemo(() => {
    if (!data || !data.notAvailable) return [];
    return groupByState(data.notAvailable);
  }, [data]);

  const edgeCases = useMemo(() => {
    if (!data || !data.edgeCases) return [];
    return [...data.edgeCases].sort((a, b) => a.state.localeCompare(b.state));
  }, [data]);

  const summary = useMemo(() => {
    if (!data || !data.summary) return null;
    return data.summary;
  }, [data]);

  const handleStateMouseEnter = (e, group) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cityDetails = group.cities.map((c) => `  ${c.city}: ${c.addressCount}`).join('\n');
    setTooltip({
      x: rect.right + 8,
      y: rect.top,
      content: `${group.state}\n${group.totalAddresses} total addresses\n\nCities:\n${cityDetails}`,
    });
  };

  const handleEdgeCaseMouseEnter = (e, edgeCase) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: rect.right + 8,
      y: rect.top,
      content: `${edgeCase.state} — Mixed Availability\n${edgeCase.availableCount} available / ${edgeCase.totalCount} total\n${edgeCase.pctAvailable.toFixed(1)}% coverage\n\nThis state has addresses both with and without mosquito/tick control service.`,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  if (!data || (!data.available?.length && !data.notAvailable?.length && !data.edgeCases?.length)) {
    return (
      <div style={styles.wrapper}>
        <h3 style={styles.title}>Mosquito/Tick Service Availability</h3>
        <p style={styles.subtitle}>Geographic service boundary analysis</p>
        <div style={styles.emptyState}>No availability data to display.</div>
      </div>
    );
  }

  const total = summary ? summary.totalAvailable + summary.totalNotAvailable : 0;
  const availablePct = total > 0 ? (summary.totalAvailable / total) * 100 : 0;
  const notAvailablePct = total > 0 ? (summary.totalNotAvailable / total) * 100 : 0;

  return (
    <div style={styles.wrapper}>
      <h3 style={styles.title}>Mosquito/Tick Service Availability</h3>
      <p style={styles.subtitle}>Geographic service boundary analysis</p>

      {/* Summary Bar */}
      {summary && (
        <div style={styles.summaryBarContainer}>
          <div style={styles.summaryBarLabel}>
            <span>
              <span style={{ ...styles.indicator, ...styles.indicatorGreen, marginRight: 6 }} />
              Available: {summary.totalAvailable}
            </span>
            <span>
              Not Available: {summary.totalNotAvailable}
              <span style={{ ...styles.indicator, ...styles.indicatorRed, marginLeft: 6 }} />
            </span>
          </div>
          <div style={styles.summaryBarTrack}>
            {availablePct > 0 && (
              <div style={{ ...styles.summaryBarAvailable, width: `${availablePct}%` }}>
                {availablePct >= 10 ? `${availablePct.toFixed(1)}%` : ''}
              </div>
            )}
            {notAvailablePct > 0 && (
              <div style={{ ...styles.summaryBarNotAvailable, width: `${notAvailablePct}%` }}>
                {notAvailablePct >= 10 ? `${notAvailablePct.toFixed(1)}%` : ''}
              </div>
            )}
          </div>
          {summary.overallPct != null && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textAlign: 'center' }}>
              Overall availability: {summary.overallPct.toFixed(1)}%
            </div>
          )}
        </div>
      )}

      {/* Two-Column Layout */}
      <div style={styles.columnsContainer}>
        {/* Available Column */}
        <div style={styles.column}>
          <div style={{ ...styles.columnHeader, ...styles.columnHeaderAvailable }}>
            <span style={{ ...styles.indicator, ...styles.indicatorGreen }} />
            Available ({availableGroups.length} {availableGroups.length === 1 ? 'state' : 'states'})
          </div>
          <div style={styles.stateCardsList}>
            {availableGroups.length === 0 ? (
              <div style={{ ...styles.emptyState, padding: '24px 0' }}>No available states</div>
            ) : (
              availableGroups.map((group) => (
                <StateCard
                  key={group.state}
                  group={group}
                  variant="available"
                  onMouseEnter={handleStateMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              ))
            )}
          </div>
        </div>

        {/* Not Available Column */}
        <div style={styles.column}>
          <div style={{ ...styles.columnHeader, ...styles.columnHeaderNotAvailable }}>
            <span style={{ ...styles.indicator, ...styles.indicatorRed }} />
            Not Available ({notAvailableGroups.length} {notAvailableGroups.length === 1 ? 'state' : 'states'})
          </div>
          <div style={styles.stateCardsList}>
            {notAvailableGroups.length === 0 ? (
              <div style={{ ...styles.emptyState, padding: '24px 0' }}>No unavailable states</div>
            ) : (
              notAvailableGroups.map((group) => (
                <StateCard
                  key={group.state}
                  group={group}
                  variant="notAvailable"
                  onMouseEnter={handleStateMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Edge Cases Section */}
      {edgeCases.length > 0 && (
        <div style={styles.edgeCasesSection}>
          <div style={styles.edgeCasesTitle}>
            <span style={{ ...styles.indicator, ...styles.indicatorAmber }} />
            Edge Cases — Mixed Availability ({edgeCases.length} {edgeCases.length === 1 ? 'state' : 'states'})
          </div>
          {edgeCases.map((ec) => (
            <EdgeCaseCard
              key={ec.state}
              edgeCase={ec}
              onMouseEnter={handleEdgeCaseMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          ))}
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
}
